import React, { useState } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { boschAPI } from '../../services/api';
import { Search, Loader2, Info, Database } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const BoschSearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const isPartNumber = /^\d/.test(query.trim());
      const params = isPartNumber ? { part_number: query.trim() } : { keyword: query.trim() };
      const res = await boschAPI.search(params);
      setResults(res.data || []);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="bosch-search-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-white">Bosch-Teilenummer Suche</h1>
          <p className="text-slate-400 mt-1 text-sm">Suchen Sie nach Bosch-Teilenummern oder ECU-Bezeichnungen</p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="z.B. 0281020074, EDC17C46..."
              className="input-field pl-10"
              data-testid="bosch-search-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="btn-primary" data-testid="bosch-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suchen'}
          </Button>
        </form>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="glass-card p-8 text-center">
            <Info className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Keine Ergebnisse gefunden</p>
            <p className="text-sm text-slate-400">
              Versuchen Sie eine andere Teilenummer oder Bezeichnung.
              Die Datenbank wird kontinuierlich erweitert.
            </p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-3" data-testid="bosch-results">
            <p className="text-sm text-slate-500 mb-4">{results.length} Ergebnis(se) gefunden</p>
            {results.map((part, i) => (
              <div key={i} className="glass-card p-5 hover:border-[#EC2229]/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-bold text-[#EC2229]">{part.part_number}</span>
                      {part.category && (
                        <span className="badge-neutral text-xs">{part.category}</span>
                      )}
                    </div>
                    <p className="text-white text-sm">{part.description?.de || part.description?.en || 'Keine Beschreibung'}</p>
                    {part.ecu_type && (
                      <p className="text-xs text-slate-500 mt-1">ECU: {part.ecu_type}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!searched && (
          <div className="glass-card p-8 text-center">
            <Database className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Bosch ECU Datenbank</p>
            <p className="text-sm text-slate-400">
              Geben Sie eine Bosch-Teilenummer oder ECU-Bezeichnung ein, um die Datenbank zu durchsuchen.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BoschSearchPage;
